/**
 * The zone list a picker offers, and how a zone is labelled.
 *
 * Shared rather than owned by the Scheduler: a patient record now carries a
 * timezone too, and the Patient module must not reach into another module's
 * domain to render its own field.
 */

/** The browser's own zone — only ever used to *suggest* one. */
export function detectBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** Every zone the runtime knows, for the picker. */
export function supportedTimezones(): string[] {
    const supported = (
        Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf;
    if (typeof supported === "function") {
        try {
            return supported("timeZone");
        } catch {
            return [detectBrowserTimezone()];
        }
    }
    return [detectBrowserTimezone()];
}

/** `(UTC+02:00) Europe/Madrid` — the picker's option label. */
export function timezoneLabel(timeZone: string, at: Date = new Date()): string {
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone,
            timeZoneName: "longOffset",
        }).formatToParts(at);
        const offset =
            parts.find((part) => part.type === "timeZoneName")?.value ?? "UTC";
        return `(${offset}) ${timeZone}`;
    } catch {
        // A stored identifier this runtime does not know. Showing it bare
        // keeps it selectable, which is what stops the field from silently
        // changing a value nobody touched.
        return timeZone;
    }
}
